import { useState } from 'react';
import { useGamification } from '../context/GamificationContext';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import {
  Trophy,
  Star,
  Flame,
  Award,
  Heart,
  Eye,
  Zap,
  Users,
  Radio,
  TrendingUp,
  DollarSign,
  Gem,
  CheckCircle,
  Clock,
  Flag,
  UserCheck,
  FileText,
  PenTool,
  Banknote,
  ChevronRight,
} from 'lucide-react';

const BADGE_ICONS = {
  'pen-tool': PenTool, 'file-text': FileText, heart: Heart, eye: Eye,
  zap: Zap, users: Users, flame: Flame, award: Award, radio: Radio,
  'trending-up': TrendingUp, star: Star, 'dollar-sign': DollarSign,
  banknote: Banknote, gem: Gem, 'check-circle': CheckCircle, clock: Clock,
  flag: Flag, 'user-check': UserCheck,
};

function XpBar({ progress }) {
  return (
    <div className="relative h-3 bg-muted/30 rounded-full overflow-hidden" data-testid="xp-bar">
      <div
        className="absolute inset-y-0 left-0 bg-gradient-to-r from-violet-600 to-fuchsia-500 rounded-full transition-all duration-700 ease-out"
        style={{ width: `${progress.progress_percent}%` }}
      />
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-[9px] font-bold text-foreground/80 drop-shadow">
          {progress.level_xp} / {progress.xp_needed} XP
        </span>
      </div>
    </div>
  );
}

function BadgeItem({ badge, compact }) {
  const Icon = BADGE_ICONS[badge.icon] || Award;
  const earned = badge.earned !== false;

  if (compact) {
    return (
      <div
        className={`w-10 h-10 rounded-xl flex items-center justify-center border transition-all ${
          earned ? 'bg-violet-600/20 border-violet-500/30' : 'bg-muted/20 border-border opacity-30'
        }`}
        title={`${badge.name}: ${badge.description}`}
        data-testid={`badge-${badge.badge_id}`}
      >
        <Icon className={`w-5 h-5 ${earned ? 'text-violet-400' : 'text-muted-foreground/40'}`} />
      </div>
    );
  }

  return (
    <div
      className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
        earned ? 'bg-violet-600/10 border-violet-500/20' : 'bg-muted/20 border-border opacity-40'
      }`}
      data-testid={`badge-${badge.badge_id}`}
    >
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${earned ? 'bg-violet-600/20' : 'bg-muted/30'}`}>
        <Icon className={`w-5 h-5 ${earned ? 'text-violet-400' : 'text-muted-foreground/40'}`} />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{badge.name}</p>
        <p className="text-xs text-muted-foreground/60 truncate">{badge.description}</p>
      </div>
      {earned && (
        <Badge className="ml-auto shrink-0 bg-green-500/20 text-green-400 text-[10px]">Earned</Badge>
      )}
    </div>
  );
}

export function GamificationMini({ profile: externalProfile }) {
  const ctx = useGamification();
  const profile = externalProfile || ctx?.profile;
  if (!profile) return null;

  return (
    <div className="flex items-center gap-2" data-testid="gamification-mini">
      <div
        className="px-2 py-0.5 rounded-full text-xs font-bold border"
        style={{ color: profile.rank_color, borderColor: `${profile.rank_color}40` }}
        data-testid="user-rank"
      >
        Lv.{profile.level}
      </div>
      <span className="text-xs font-medium" style={{ color: profile.rank_color }}>{profile.rank}</span>
      {profile.post_streak > 0 && (
        <span className="text-xs text-orange-400 flex items-center gap-0.5">
          <Flame className="w-3 h-3" /> {profile.post_streak}
        </span>
      )}
    </div>
  );
}

export function GamificationCard({ profile: externalProfile, expanded = false }) {
  const ctx = useGamification();
  const profile = externalProfile || ctx?.profile;
  const [showAll, setShowAll] = useState(expanded);

  if (!profile) return null;

  const earnedBadges = (profile.badges || []).filter(b => b.earned !== false);
  const unearnedBadges = (profile.badges || []).filter(b => b.earned === false);

  return (
    <Card className="bg-card/50 border border-border overflow-hidden" data-testid="gamification-card">
      {/* Level header */}
      <div className="p-4 pb-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center font-black text-lg"
              style={{ background: `${profile.rank_color}20`, color: profile.rank_color }}
              data-testid="level-badge"
            >
              {profile.level}
            </div>
            <div>
              <h3 className="font-bold text-foreground" data-testid="user-rank-name">{profile.rank}</h3>
              <p className="text-xs text-muted-foreground/60">{profile.total_xp.toLocaleString()} XP Total</p>
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-1 text-xs text-muted-foreground/70">
              <Trophy className="w-3.5 h-3.5" />
              {profile.badges_earned || earnedBadges.length} / {profile.badges_total || (earnedBadges.length + unearnedBadges.length)}
            </div>
          </div>
        </div>

        {/* XP Progress Bar */}
        {profile.progress && <XpBar progress={profile.progress} />}
        {profile.progress && (
          <p className="text-[10px] text-muted-foreground/50 mt-1 text-right">
            {profile.progress.xp_to_next} XP to Level {profile.level + 1}
          </p>
        )}
      </div>

      {/* Streaks */}
      {(profile.post_streak > 0 || profile.login_streak > 0) && (
        <div className="px-4 pb-3 flex gap-2">
          {profile.post_streak > 0 && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-orange-500/10 border border-orange-500/20" data-testid="post-streak">
              <Flame className="w-3.5 h-3.5 text-orange-400" />
              <span className="text-xs text-orange-300 font-medium">{profile.post_streak} Day Post Streak</span>
            </div>
          )}
          {profile.login_streak > 0 && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20" data-testid="login-streak">
              <Star className="w-3.5 h-3.5 text-cyan-400" />
              <span className="text-xs text-cyan-300 font-medium">{profile.login_streak} Day Login Streak</span>
            </div>
          )}
        </div>
      )}

      {/* Badges */}
      <div className="px-4 pb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-muted-foreground/80">Badges</span>
          {!showAll && earnedBadges.length > 0 && (
            <button onClick={() => setShowAll(true)} className="text-xs text-violet-400 hover:text-violet-300 flex items-center gap-0.5">
              View All <ChevronRight className="w-3 h-3" />
            </button>
          )}
        </div>

        {showAll ? (
          <div className="space-y-2">
            {earnedBadges.map(b => <BadgeItem key={b.badge_id} badge={b} />)}
            {unearnedBadges.length > 0 && (
              <>
                <p className="text-[10px] text-muted-foreground/50 mt-3 mb-1">Locked</p>
                {unearnedBadges.map(b => <BadgeItem key={b.badge_id} badge={b} />)}
              </>
            )}
          </div>
        ) : (
          <div className="flex gap-1.5 flex-wrap">
            {earnedBadges.slice(0, 8).map(b => <BadgeItem key={b.badge_id} badge={b} compact />)}
            {earnedBadges.length === 0 && (
              <p className="text-xs text-muted-foreground/50">No badges yet. Keep engaging!</p>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
